/**
 * MarketSnapshotsSettings Component
 *
 * Settings screen for configuring market snapshot fields.
 * Allows users to define custom fields for capturing market conditions.
 *
 * Phase 2 Implementation:
 * - Field list display with SortableList
 * - Dirty-state tracking with useLiveSettings hook
 * - Save/Discard buttons
 * - Delete confirmation modal
 * - Cross-tab sync via syncCoordinatorService
 *
 * Phase 3 Implementation:
 * - Add/Edit Field Modal for all 6 field types
 * - Dropdown options with add, edit, delete, and reorder
 * - Two-tier validation (errors block save)
 * - Type locking on edit
 * - pinToLive toggle
 */

// 1. External dependencies
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Plus, Pencil, Trash2, AlertTriangle, Camera, Pin } from 'lucide-react';

// 2. Internal absolute imports
import {
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Button,
  Chip,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  SortableList,
  DragHandle,
  EmptyState,
  useToastNotification,
  Input,
  Select,
  SelectItem,
  Switch,
} from '@enfomentis/ui';
import type { DragHandleProps } from '@enfomentis/ui';

// 3. Services
import { marketSnapshotSettingsService } from '@/services/MarketSnapshotSettingsService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';

// 4. Hooks
import { useLiveSettings } from '../PreTrading/hooks/useLiveSettings';

// 5. Types
import type { MarketSnapshotSettings } from '@/types/schema/MarketSnapshotSettings';
import type { SnapshotFieldDefinition, SnapshotFieldType } from '@/types/schema/MarketSnapshotSettings';
import type { DropdownOption } from '@/types/shared';

// 6. Utils
import { generateUUID } from '@/utils';

// ============================================================================
// Types
// ============================================================================

/**
 * Validation errors for field editing (blocking errors only)
 */
interface FieldValidationErrors {
  name?: string;
  options?: string;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Field type display configuration for type badges
 */
const FIELD_TYPE_CONFIG: Record<
  SnapshotFieldType,
  { label: string; color: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger' }
> = {
  checkbox: { label: 'Checkbox', color: 'success' },
  text: { label: 'Text', color: 'primary' },
  number: { label: 'Number', color: 'secondary' },
  dropdown: { label: 'Dropdown', color: 'warning' },
  image: { label: 'Image', color: 'default' },
  url: { label: 'URL', color: 'default' },
};

/**
 * Field type options for the type select dropdown
 */
const FIELD_TYPE_OPTIONS: Array<{ key: SnapshotFieldType; label: string }> = [
  { key: 'checkbox', label: 'Checkbox' },
  { key: 'text', label: 'Text' },
  { key: 'number', label: 'Number' },
  { key: 'dropdown', label: 'Dropdown' },
  { key: 'image', label: 'Image' },
  { key: 'url', label: 'URL' },
];

// ============================================================================
// Validation Helpers
// ============================================================================

/**
 * Normalize whitespace: trim and collapse multiple spaces to single space
 */
function normalizeWhitespace(str: string): string {
  return str.trim().replace(/\s+/g, ' ');
}

/**
 * Validate a field draft against all other fields
 * Returns blocking errors only (no warnings for this modal)
 */
function validateFieldDraft(
  draft: SnapshotFieldDefinition,
  allFields: SnapshotFieldDefinition[],
  editingFieldId: string | 'new'
): FieldValidationErrors {
  const errors: FieldValidationErrors = {};
  const normalizedName = normalizeWhitespace(draft.name);

  // Empty field name
  if (!normalizedName) {
    errors.name = 'Field name is required';
  } else {
    // Duplicate field names (case-insensitive, whitespace-normalized)
    const isDuplicate = allFields.some(
      (f) =>
        f.id !== (editingFieldId === 'new' ? draft.id : editingFieldId) &&
        normalizeWhitespace(f.name).toLowerCase() === normalizedName.toLowerCase()
    );
    if (isDuplicate) {
      errors.name = 'A field with this name already exists';
    }
  }

  // Dropdown-specific validation
  if (draft.type === 'dropdown') {
    const options = draft.options ?? [];

    // Dropdown with zero options
    if (options.length === 0) {
      errors.options = 'At least one option is required';
    } else {
      // Check for empty labels
      const hasEmptyLabel = options.some((o) => !normalizeWhitespace(o.label));
      if (hasEmptyLabel) {
        errors.options = 'Option labels cannot be empty';
      }

      // Check for duplicate option labels (case-insensitive, whitespace-normalized)
      const normalizedLabels = options.map((o) => normalizeWhitespace(o.label).toLowerCase());
      const uniqueLabels = new Set(normalizedLabels);
      if (uniqueLabels.size !== normalizedLabels.length) {
        errors.options = 'Duplicate option labels are not allowed';
      }

      // Check for duplicate option IDs (defensive)
      const optionIds = options.map((o) => o.id);
      const uniqueIds = new Set(optionIds);
      if (uniqueIds.size !== optionIds.length) {
        errors.options = 'Duplicate option IDs detected';
      }
    }
  }

  return errors;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Market Snapshots Settings Screen
 *
 * Displays a sortable list of snapshot field definitions with drag-and-drop
 * reordering, delete functionality, and dirty-state tracking.
 */
export const MarketSnapshotsSettings: React.FC = () => {
  const { showToast } = useToastNotification();

  // ============================================================================
  // Local State - Field List
  // ============================================================================

  // Local fields for editing (source of truth while editing)
  const [localFields, setLocalFields] = useState<SnapshotFieldDefinition[]>([]);

  // Saving state for UI feedback
  const [isSaving, setIsSaving] = useState(false);

  // Delete confirmation modal state
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // External update conflict banner dismiss state
  const [dismissedExternalUpdate, setDismissedExternalUpdate] = useState(false);

  // ============================================================================
  // Local State - Edit Modal
  // ============================================================================

  // Edit modal state: null = closed, 'new' = adding, string = editing field id
  const [editingFieldId, setEditingFieldId] = useState<string | 'new' | null>(null);

  // Working copy of the field being edited
  const [editingDraft, setEditingDraft] = useState<SnapshotFieldDefinition | null>(null);

  // Validation errors for the edit modal
  const [editModalErrors, setEditModalErrors] = useState<FieldValidationErrors>({});

  // Input value for adding new dropdown option
  const [newOptionValue, setNewOptionValue] = useState('');

  // Currently editing option ID (for inline label editing)
  const [editingOptionId, setEditingOptionId] = useState<string | null>(null);

  // Delete option confirmation modal state
  const [deleteOptionConfirmId, setDeleteOptionConfirmId] = useState<string | null>(null);

  // ============================================================================
  // Refs
  // ============================================================================

  // Baseline for dirty tracking (stores JSON string)
  const lastSavedRef = useRef<string>('[]');

  // Track if initial load has happened
  const hasInitializedRef = useRef(false);

  // Track previous hasQueuedUpdate for transition detection
  const prevHasQueuedUpdateRef = useRef(false);

  // ============================================================================
  // Dirty State Computation
  // ============================================================================

  const isDirty = JSON.stringify(localFields) !== lastSavedRef.current;

  // ============================================================================
  // Live Settings Hook
  // ============================================================================

  const handleExternalUpdate = useCallback(() => {
    showToast({
      title: 'Settings Updated',
      description: 'Market Snapshots settings updated from another tab',
      color: 'success',
    });
  }, [showToast]);

  const {
    settings,
    setSettings,
    isLoading,
    hasQueuedUpdate,
  } = useLiveSettings<MarketSnapshotSettings>({
    table: 'market_snapshot_settings',
    fetchSettings: () => marketSnapshotSettingsService.getCurrentSettings(),
    isDirty,
    onExternalUpdate: handleExternalUpdate,
  });

  // ============================================================================
  // Conflict Banner Logic
  // ============================================================================

  const showConflictBanner = hasQueuedUpdate && isDirty && !dismissedExternalUpdate;

  // Reset dismissedExternalUpdate when hasQueuedUpdate transitions false -> true
  useEffect(() => {
    const wasQueued = prevHasQueuedUpdateRef.current;
    const isQueued = hasQueuedUpdate;
    prevHasQueuedUpdateRef.current = hasQueuedUpdate;

    // Transition false -> true means new external update arrived
    if (!wasQueued && isQueued) {
      setDismissedExternalUpdate(false);
    }
  }, [hasQueuedUpdate]);

  // ============================================================================
  // Settings Sync Effect
  // ============================================================================

  // Initialize local state from settings on first load
  // Sync local state when not dirty and settings change
  useEffect(() => {
    if (!settings) return;

    const settingsFields = settings.snapshotFields;
    const settingsFieldsJson = JSON.stringify(settingsFields);

    // First load: initialize both local state and baseline
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      setLocalFields(settingsFields);
      lastSavedRef.current = settingsFieldsJson;
      return;
    }

    // External update while not dirty: sync local state
    if (!isDirty) {
      if (settingsFieldsJson !== lastSavedRef.current) {
        setLocalFields(settingsFields);
        lastSavedRef.current = settingsFieldsJson;
      }
    }
  }, [settings, isDirty]);

  // ============================================================================
  // Computed Values
  // ============================================================================

  // Check if edit modal has blocking errors
  const hasEditModalErrors = useMemo(
    () => Object.keys(editModalErrors).length > 0,
    [editModalErrors]
  );

  // Get option label for delete confirmation
  const deleteOptionLabel = useMemo(() => {
    if (!deleteOptionConfirmId || !editingDraft) return '';
    return editingDraft.options?.find((o) => o.id === deleteOptionConfirmId)?.label || 'this option';
  }, [deleteOptionConfirmId, editingDraft]);

  // ============================================================================
  // Handler Functions - Field List
  // ============================================================================

  /**
   * Handle field reordering from SortableList
   */
  const handleReorder = useCallback((reorderedFields: SnapshotFieldDefinition[]) => {
    setLocalFields(reorderedFields);
  }, []);

  /**
   * Handle delete button click - open confirmation modal
   */
  const handleDeleteClick = useCallback((fieldId: string) => {
    setDeleteConfirmId(fieldId);
  }, []);

  /**
   * Handle delete confirmation - remove field from local state
   */
  const handleDeleteConfirm = useCallback(() => {
    if (!deleteConfirmId) return;

    setLocalFields(prev => prev.filter(f => f.id !== deleteConfirmId));
    setDeleteConfirmId(null);
  }, [deleteConfirmId]);

  /**
   * Handle delete cancellation
   */
  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmId(null);
  }, []);

  /**
   * Save settings - persist via service, notify sync, update baseline
   */
  const handleSaveSettings = useCallback(async () => {
    if (!settings) return;

    setIsSaving(true);
    try {
      // Persist via service
      const updatedSettings = await marketSnapshotSettingsService.updateSnapshotFields(localFields);

      // Notify other tabs of the change
      syncCoordinatorService.notifyChange('market_snapshot_settings', settings.id);

      // Update hook state with returned entity
      setSettings(updatedSettings);

      // Update baseline ref from returned settings (avoids drift if service dedupes/normalizes)
      lastSavedRef.current = JSON.stringify(updatedSettings.snapshotFields);

      // Sync local state with what was actually persisted
      setLocalFields(updatedSettings.snapshotFields);

      // Reset conflict banner state
      setDismissedExternalUpdate(false);

      showToast({
        title: 'Settings Saved',
        description: 'Market Snapshots settings updated',
        color: 'success',
      });
    } catch (err) {
      console.error('Failed to save settings:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      showToast({
        title: 'Save Failed',
        description: errorMessage.includes('validation') ? errorMessage : 'Your changes are preserved. Please try again.',
        color: 'danger',
      });
    } finally {
      setIsSaving(false);
    }
  }, [settings, localFields, setSettings, showToast]);

  /**
   * Discard settings changes - reset to baseline
   */
  const handleDiscardSettings = useCallback(() => {
    const baseline = JSON.parse(lastSavedRef.current) as SnapshotFieldDefinition[];
    setLocalFields(baseline);
    setDismissedExternalUpdate(false);
  }, []);

  /**
   * Accept external changes - reset to current settings
   */
  const handleAcceptExternalChanges = useCallback(() => {
    if (!settings) return;

    setLocalFields(settings.snapshotFields);
    lastSavedRef.current = JSON.stringify(settings.snapshotFields);
    setDismissedExternalUpdate(false);
  }, [settings]);

  /**
   * Keep editing - dismiss the conflict banner
   */
  const handleKeepEditing = useCallback(() => {
    setDismissedExternalUpdate(true);
  }, []);

  // ============================================================================
  // Handler Functions - Edit Modal
  // ============================================================================

  /**
   * Open the add field modal with a new empty field
   */
  const handleOpenAddModal = useCallback(() => {
    const newField: SnapshotFieldDefinition = {
      id: generateUUID(),
      name: '',
      type: 'checkbox',
      pinToLive: false,
    };
    setEditingFieldId('new');
    setEditingDraft(newField);
    setEditModalErrors({});
    setNewOptionValue('');
    setEditingOptionId(null);
    setDeleteOptionConfirmId(null);
  }, []);

  /**
   * Open the edit field modal with a copy of the field
   */
  const handleOpenEditModal = useCallback((field: SnapshotFieldDefinition) => {
    // Deep copy the field including options
    const fieldCopy: SnapshotFieldDefinition = {
      id: field.id,
      name: field.name,
      type: field.type,
      pinToLive: field.pinToLive,
    };
    if (field.description !== undefined) {
      fieldCopy.description = field.description;
    }
    if (field.options) {
      fieldCopy.options = field.options.map((o) => ({ ...o }));
    }

    setEditingFieldId(field.id);
    setEditingDraft(fieldCopy);
    setEditModalErrors({});
    setNewOptionValue('');
    setEditingOptionId(null);
    setDeleteOptionConfirmId(null);
  }, []);

  /**
   * Update the editing draft and run validation
   */
  const handleEditDraftChange = useCallback(
    (key: keyof SnapshotFieldDefinition, value: string | boolean | SnapshotFieldType | DropdownOption[]) => {
      setEditingDraft((prev) => {
        if (!prev) return prev;

        let updated: SnapshotFieldDefinition;

        // Handle type change - clear options when switching away from dropdown
        if (key === 'type' && value !== 'dropdown') {
          const { options: _options, ...rest } = prev;
          updated = { ...rest, [key]: value as SnapshotFieldType };
          setNewOptionValue('');
        } else if (key === 'type' && value === 'dropdown' && !prev.options) {
          // Initialize empty options array when switching to dropdown
          updated = { ...prev, [key]: value as SnapshotFieldType, options: [] };
        } else {
          updated = { ...prev, [key]: value };
        }

        // Run validation
        const errors = validateFieldDraft(updated, localFields, editingFieldId ?? 'new');
        setEditModalErrors(errors);

        return updated;
      });
    },
    [localFields, editingFieldId]
  );

  /**
   * Add a new dropdown option
   */
  const handleAddOption = useCallback(() => {
    const trimmedValue = normalizeWhitespace(newOptionValue);
    if (!trimmedValue || !editingDraft) return;

    const currentOptions = editingDraft.options ?? [];

    // Check for duplicate labels (case-insensitive)
    const isDuplicate = currentOptions.some(
      (o) => normalizeWhitespace(o.label).toLowerCase() === trimmedValue.toLowerCase()
    );
    if (isDuplicate) {
      showToast({
        title: 'Duplicate Option',
        description: 'An option with this label already exists',
        color: 'warning',
      });
      return;
    }

    const newOption: DropdownOption = {
      id: generateUUID(),
      label: trimmedValue,
    };

    handleEditDraftChange('options', [...currentOptions, newOption]);
    setNewOptionValue('');
  }, [newOptionValue, editingDraft, handleEditDraftChange, showToast]);

  /**
   * Handle option delete button click - open confirmation
   */
  const handleRemoveOptionClick = useCallback((optionId: string) => {
    setDeleteOptionConfirmId(optionId);
  }, []);

  /**
   * Confirm and remove a dropdown option
   */
  const handleRemoveOptionConfirm = useCallback(() => {
    if (!deleteOptionConfirmId || !editingDraft) return;

    const currentOptions = editingDraft.options ?? [];
    handleEditDraftChange('options', currentOptions.filter((o) => o.id !== deleteOptionConfirmId));
    setDeleteOptionConfirmId(null);
  }, [deleteOptionConfirmId, editingDraft, handleEditDraftChange]);

  /**
   * Cancel option delete
   */
  const handleRemoveOptionCancel = useCallback(() => {
    setDeleteOptionConfirmId(null);
  }, []);

  /**
   * Reorder dropdown options
   */
  const handleReorderOptions = useCallback(
    (reorderedOptions: DropdownOption[]) => {
      handleEditDraftChange('options', reorderedOptions);
    },
    [handleEditDraftChange]
  );

  /**
   * Start editing an option label
   */
  const handleOptionLabelEdit = useCallback((optionId: string) => {
    setEditingOptionId(optionId);
  }, []);

  /**
   * Update an option label
   * Note: Duplicate label validation happens in validateFieldDraft via handleEditDraftChange
   */
  const handleOptionLabelChange = useCallback(
    (optionId: string, newLabel: string) => {
      if (!editingDraft) return;

      const currentOptions = editingDraft.options ?? [];
      const updatedOptions = currentOptions.map((o) =>
        o.id === optionId ? { ...o, label: newLabel } : o
      );
      handleEditDraftChange('options', updatedOptions);
    },
    [editingDraft, handleEditDraftChange]
  );

  /**
   * Stop editing an option label
   */
  const handleOptionLabelBlur = useCallback(() => {
    setEditingOptionId(null);
  }, []);

  /**
   * Save the edited field to local state (does NOT persist to DB)
   */
  const handleSaveEditModal = useCallback(() => {
    if (!editingDraft || !editingFieldId) return;

    // Final validation
    const errors = validateFieldDraft(editingDraft, localFields, editingFieldId);
    if (Object.keys(errors).length > 0) {
      setEditModalErrors(errors);
      return;
    }

    // Normalize the field before saving
    const normalizedField: SnapshotFieldDefinition = {
      id: editingDraft.id,
      name: normalizeWhitespace(editingDraft.name),
      type: editingDraft.type,
      pinToLive: editingDraft.pinToLive,
    };

    // Only include description if non-empty
    const trimmedDescription = editingDraft.description?.trim();
    if (trimmedDescription) {
      normalizedField.description = trimmedDescription;
    }

    // Only include options for dropdown type
    if (editingDraft.type === 'dropdown' && editingDraft.options && editingDraft.options.length > 0) {
      // Normalize option labels
      normalizedField.options = editingDraft.options.map((o) => ({
        id: o.id,
        label: normalizeWhitespace(o.label),
      }));
    }

    // Update local fields
    if (editingFieldId === 'new') {
      setLocalFields((prev) => [...prev, normalizedField]);
    } else {
      setLocalFields((prev) =>
        prev.map((f) => (f.id === editingFieldId ? normalizedField : f))
      );
    }

    // Close modal
    setEditingFieldId(null);
    setEditingDraft(null);
    setEditModalErrors({});
    setNewOptionValue('');
    setEditingOptionId(null);
    setDeleteOptionConfirmId(null);
  }, [editingDraft, editingFieldId, localFields]);

  /**
   * Cancel editing and close the modal
   */
  const handleCancelEditModal = useCallback(() => {
    setEditingFieldId(null);
    setEditingDraft(null);
    setEditModalErrors({});
    setNewOptionValue('');
    setEditingOptionId(null);
    setDeleteOptionConfirmId(null);
  }, []);

  // ============================================================================
  // Field Row Renderer
  // ============================================================================

  const renderFieldRow = useCallback(
    (field: SnapshotFieldDefinition, dragHandleProps: DragHandleProps) => {
      const typeConfig = FIELD_TYPE_CONFIG[field.type];

      return (
        <Card
          shadow="sm"
          radius="sm"
          className={dragHandleProps.isDragging ? 'opacity-50 shadow-lg' : ''}
        >
          <CardBody className="p-3">
            <div className="flex items-center gap-3">
              <DragHandle dragHandleProps={dragHandleProps} />

              {/* Tappable content area */}
              <button
                type="button"
                className="flex-1 text-left min-w-0"
                onClick={() => handleOpenEditModal(field)}
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium truncate">
                    {field.name}
                  </span>
                </div>
                {field.description && (
                  <div className="text-sm text-text-secondary truncate">
                    {field.description}
                  </div>
                )}
              </button>

              {field.pinToLive && (
                <Pin className="w-4 h-4 text-primary" aria-label="Pinned to live dashboard" />
              )}

              <Chip size="sm" color={typeConfig.color} variant="flat">
                {typeConfig.label}
              </Chip>

              <Button
                isIconOnly
                size="sm"
                variant="light"
                onPress={() => handleOpenEditModal(field)}
                aria-label={`Edit ${field.name}`}
              >
                <Pencil className="w-4 h-4" />
              </Button>

              <Button
                isIconOnly
                size="sm"
                variant="light"
                color="danger"
                onPress={() => handleDeleteClick(field.id)}
                aria-label={`Delete ${field.name}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </CardBody>
        </Card>
      );
    },
    [handleOpenEditModal, handleDeleteClick]
  );

  // ============================================================================
  // Dropdown Option Row Renderer
  // ============================================================================

  const renderOptionRow = useCallback(
    (option: DropdownOption, dragHandleProps: DragHandleProps) => {
      const isEditing = editingOptionId === option.id;

      return (
        <Card
          shadow="sm"
          radius="sm"
          className={`${dragHandleProps.isDragging ? 'opacity-50 shadow-lg' : ''}`}
        >
          <CardBody className="py-1.5 px-2">
            <div className="flex items-center gap-2">
              <DragHandle dragHandleProps={dragHandleProps} />

              {isEditing ? (
                <Input
                  size="sm"
                  value={option.label}
                  onChange={(e) => handleOptionLabelChange(option.id, e.target.value)}
                  onBlur={handleOptionLabelBlur}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleOptionLabelBlur();
                    }
                  }}
                  autoFocus
                  classNames={{ base: 'flex-1', input: 'text-base' }}
                />
              ) : (
                <button
                  type="button"
                  className="flex-1 text-left text-sm truncate"
                  onClick={() => handleOptionLabelEdit(option.id)}
                >
                  {option.label}
                </button>
              )}

              <Button
                isIconOnly
                size="sm"
                variant="light"
                color="danger"
                onPress={() => handleRemoveOptionClick(option.id)}
                aria-label={`Delete ${option.label}`}
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </CardBody>
        </Card>
      );
    },
    [editingOptionId, handleOptionLabelChange, handleOptionLabelBlur, handleOptionLabelEdit, handleRemoveOptionClick]
  );

  // ============================================================================
  // Delete Field Name for Modal
  // ============================================================================

  const deleteFieldName = deleteConfirmId
    ? localFields.find(f => f.id === deleteConfirmId)?.name || 'this field'
    : '';

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <>
      <Card shadow="none" radius="none" className="h-full flex flex-col">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-text-primary">
              Snapshot Fields
            </h3>
            <p className="text-sm text-text-secondary">
              Define your template for capturing market state across any instrument and timeframe
            </p>
          </div>
          <Button
            color="primary"
            size="sm"
            startContent={<Plus size={16} />}
            onPress={handleOpenAddModal}
          >
            Add Field
          </Button>
        </CardHeader>

        <CardBody className="flex-1 min-h-0">
          {/* External Update Conflict Banner */}
          {showConflictBanner && (
            <div className="rounded-lg bg-warning-50 border border-warning-200 p-4 mb-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-warning-600 shrink-0" />
                  <span className="text-sm text-warning-700">
                    Market Snapshots settings updated in another tab
                  </span>
                </div>
                <div className="flex flex-col gap-2 md:flex-row">
                  <Button size="sm" variant="flat" onPress={handleKeepEditing}>
                    Keep editing
                  </Button>
                  <Button
                    size="sm"
                    color="warning"
                    onPress={handleAcceptExternalChanges}
                  >
                    Accept changes
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {isLoading ? (
            <div className="text-center py-8">
              <p className="text-text-secondary">Loading settings...</p>
            </div>
          ) : localFields.length === 0 ? (
            /* Empty State */
            <EmptyState
              title="No snapshot fields configured"
              description="Add fields to capture market conditions before trading sessions."
              icon={<Camera size={24} />}
              action={
                <Button
                  color="primary"
                  size="sm"
                  startContent={<Plus size={16} />}
                  onPress={handleOpenAddModal}
                >
                  Add First Field
                </Button>
              }
            />
          ) : (
            /* Field List */
            <SortableList
              items={localFields}
              onReorder={handleReorder}
              className="space-y-2"
              renderItem={renderFieldRow}
            />
          )}
        </CardBody>

        {/* Footer with Save/Discard - only visible when dirty */}
        {isDirty && (
          <CardFooter className="flex items-center justify-between gap-4 border-t border-default-200">
            <div className="flex flex-col gap-1">
              <span className="text-sm text-warning-600">Unsaved changes</span>
            </div>
            <div className="flex gap-2">
              <Button variant="flat" onPress={handleDiscardSettings} isDisabled={isSaving}>
                Discard
              </Button>
              <Button
                color="primary"
                onPress={handleSaveSettings}
                isDisabled={isSaving}
                isLoading={isSaving}
              >
                Save
              </Button>
            </div>
          </CardFooter>
        )}
      </Card>

      {/* Delete Field Confirmation Modal */}
      <Modal
        isOpen={!!deleteConfirmId}
        onClose={handleDeleteCancel}
        size="sm"
        placement="center"
      >
        <ModalContent>
          <ModalHeader>
            Delete &quot;{deleteFieldName}&quot;?
          </ModalHeader>
          <ModalBody>
            <p className="text-text-secondary">
              This field will be removed from your snapshot configuration.
            </p>
            <p className="text-warning-600 text-sm mt-2">
              Warning: Historical market state data referencing this field will not be migrated or cleaned up.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="flat" onPress={handleDeleteCancel}>
              Cancel
            </Button>
            <Button color="danger" onPress={handleDeleteConfirm}>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add/Edit Field Modal */}
      <Modal
        isOpen={editingFieldId !== null && deleteOptionConfirmId === null}
        onClose={handleCancelEditModal}
        size="lg"
        placement="top"
        scrollBehavior="inside"
      >
        <ModalContent>
          <ModalHeader>
            {editingFieldId === 'new' ? 'Add Field' : 'Edit Field'}
          </ModalHeader>
          <ModalBody className="gap-4">
            {editingDraft && (
              <>
                {/* Field Name */}
                <Input
                  label="Field Name"
                  placeholder="e.g., Trend Direction"
                  value={editingDraft.name}
                  onChange={(e) => handleEditDraftChange('name', e.target.value)}
                  isInvalid={!!editModalErrors.name}
                  errorMessage={editModalErrors.name}
                  isRequired
                  autoFocus
                  classNames={{ input: 'text-base' }}
                />

                {/* Description */}
                <Input
                  label="Description (optional)"
                  placeholder="Brief description of this field"
                  value={editingDraft.description || ''}
                  onChange={(e) => handleEditDraftChange('description', e.target.value)}
                  classNames={{ input: 'text-base' }}
                />

                {/* Field Type */}
                <Select
                  label="Field Type"
                  selectedKeys={new Set([editingDraft.type])}
                  onSelectionChange={(keys) => {
                    if (keys !== 'all') {
                      const selected = Array.from(keys)[0] as SnapshotFieldType;
                      if (selected) {
                        handleEditDraftChange('type', selected);
                      }
                    }
                  }}
                  isDisabled={editingFieldId !== 'new'}
                  description={editingFieldId !== 'new' ? 'Field type cannot be changed after creation' : undefined}
                >
                  {FIELD_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.key}>{option.label}</SelectItem>
                  ))}
                </Select>

                {/* Pin to Live Toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Pin to Live Dashboard</p>
                    <p className="text-xs text-text-secondary">
                      Show this field&apos;s value during live trading
                    </p>
                  </div>
                  <Switch
                    isSelected={editingDraft.pinToLive}
                    onValueChange={(checked) => handleEditDraftChange('pinToLive', checked)}
                  />
                </div>

                {/* Dropdown Options Section */}
                {editingDraft.type === 'dropdown' && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium">
                        Dropdown Options
                        {editModalErrors.options && (
                          <span className="text-danger ml-2 font-normal text-xs">
                            {editModalErrors.options}
                          </span>
                        )}
                      </p>
                    </div>

                    {/* Options List */}
                    {editingDraft.options && editingDraft.options.length > 0 && (
                      <SortableList
                        items={editingDraft.options}
                        onReorder={handleReorderOptions}
                        className="space-y-1"
                        renderItem={renderOptionRow}
                      />
                    )}

                    {/* Add Option Input */}
                    <div className="flex gap-2">
                      <Input
                        placeholder="New option label"
                        value={newOptionValue}
                        onChange={(e) => setNewOptionValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddOption();
                          }
                        }}
                        size="sm"
                        classNames={{ base: 'flex-1', input: 'text-base' }}
                      />
                      <Button
                        size="sm"
                        variant="flat"
                        color="primary"
                        onPress={handleAddOption}
                        isDisabled={!newOptionValue.trim()}
                      >
                        Add
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={handleCancelEditModal}>
              Cancel
            </Button>
            <Button
              color="primary"
              onPress={handleSaveEditModal}
              isDisabled={hasEditModalErrors}
            >
              {editingFieldId === 'new' ? 'Add' : 'Save'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Option Confirmation Modal */}
      <Modal
        isOpen={deleteOptionConfirmId !== null}
        onClose={handleRemoveOptionCancel}
        size="sm"
        placement="center"
      >
        <ModalContent>
          <ModalHeader>
            Delete &quot;{deleteOptionLabel}&quot;?
          </ModalHeader>
          <ModalBody>
            <p className="text-text-secondary">
              This option will be removed from the dropdown.
            </p>
            <p className="text-warning-600 text-sm mt-2">
              Warning: Existing values using this option will retain the old label.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="flat" onPress={handleRemoveOptionCancel}>
              Cancel
            </Button>
            <Button color="danger" onPress={handleRemoveOptionConfirm}>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
